import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PartyPopper } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const fmt = (s) => new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export const PromoPanel = () => {
  const qc = useQueryClient();
  const { data: promo, isLoading } = useQuery({ queryKey: ["admin-promo"], queryFn: () => api.get("/admin/promo").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", start: "", end: "" });
  const [busy, setBusy] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-promo"] }); qc.invalidateQueries({ queryKey: ["admin-users"] }); };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promo", { name: form.name, start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() });
      toast.success(`"${data.name}" scheduled. ${data.extended.length} subscriber renewals pushed back.`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this event? If it hasn't ended, everyone loses free Pro and subscribers' renewal dates go back to normal.")) return;
    setBusy(true);
    try { await api.delete("/admin/promo"); toast.success("Event removed"); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const now = new Date().toISOString();
  const status = promo && (promo.end <= now ? "Ended" : promo.start <= now ? "Live now" : "Scheduled");
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-promo">
      <div className="flex items-center gap-2"><PartyPopper className="h-4 w-4 text-[#FBBF24]" /><div className="lf-label">Free Pro event</div></div>
      <p className="mt-2 text-xs text-[#A8A090]">Everyone gets Pro between the start and end. Paying subscribers' next charge moves back by the length of the event.</p>
      {isLoading ? null : promo ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4" data-testid="admin-promo-current">
          <div>
            <div className="text-[#F3EBDD]">{promo.name} <span className="ml-2 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#FBBF24]" data-testid="admin-promo-status">{status}</span></div>
            <div className="mt-1 text-xs text-[#A8A090]">{fmt(promo.start)} → {fmt(promo.end)} · {promo.extended?.length || 0} renewals deferred</div>
          </div>
          <Button variant="outline" size="sm" onClick={remove} disabled={busy} data-testid="admin-promo-remove">{status === "Ended" ? "Clear" : "Cancel event"}</Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-[#A8A090]">Event name<Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="NaNoWriMo" className="mt-1 w-56" data-testid="admin-promo-name" /></label>
          <label className="text-xs text-[#A8A090]">Starts<Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1" data-testid="admin-promo-start" /></label>
          <label className="text-xs text-[#A8A090]">Ends<Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1" data-testid="admin-promo-end" /></label>
          <Button onClick={create} disabled={busy || !form.name || !form.start || !form.end} data-testid="admin-promo-create">Schedule event</Button>
        </div>
      )}
    </section>
  );
};
